import { notifyError, notifySuccess } from "@/lib/notification";
import * as bookmarkService from "@/services/bookmark.service";
import { BookmarkType } from "@/services/bookmark.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const bookmarkKey = {
  all: ["bookmarks"] as const,
  list: () => [...bookmarkKey.all, "list"] as const,
};

// ─── Queries ──────────────────────────────────────────────────────────────────

export const useBookmarksQuery = () =>
  useQuery({
    queryKey: bookmarkKey.list(),
    queryFn: bookmarkService.fetchBookmarks,
  });

// ─── Derived helpers ──────────────────────────────────────────────────────────

/**
 * Returns convenience helpers for a specific internship:
 * - isBookmarked: boolean
 * - bookmarkId: string | undefined
 */
export const useIsBookmarked = (internshipId: string) => {
  const { data: bookmarks = [] } = useBookmarksQuery();
  const match = bookmarks.find((b) => b.internship_id === internshipId);
  return {
    isBookmarked: !!match,
    bookmarkId: match?.id,
  };
};

// ─── Mutations ────────────────────────────────────────────────────────────────

export const useAddBookmarkMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<BookmarkType, Error, string>({
    mutationFn: bookmarkService.addBookmark,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookmarkKey.all });
      notifySuccess("Internship bookmarked! 🔖");
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.detail?.[0]?.msg ||
        error?.response?.data?.message ||
        "Failed to bookmark internship";
      notifyError(msg);
    },
  });
};

export const useRemoveBookmarkMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: bookmarkService.removeBookmark,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookmarkKey.all });
      notifySuccess("Bookmark removed.");
    },
    onError: (error: any) => {
      const msg =
        error?.response?.data?.message || "Failed to remove bookmark";
      notifyError(msg);
    },
  });
};

/**
 * Single toggle hook — handles both add and remove.
 * Usage: const { toggle, isPending } = useBookmarkToggle(internshipId)
 */
export const useBookmarkToggle = (internshipId: string) => {
  const { isBookmarked, bookmarkId } = useIsBookmarked(internshipId);
  const addMutation = useAddBookmarkMutation();
  const removeMutation = useRemoveBookmarkMutation();

  const toggle = () => {
    if (isBookmarked && bookmarkId) {
      removeMutation.mutate(bookmarkId);
    } else {
      addMutation.mutate(internshipId);
    }
  };

  return {
    isBookmarked,
    toggle,
    isPending: addMutation.isPending || removeMutation.isPending,
  };
};
